import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@akuna/ui";
import { Separator } from "@akuna/ui";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SegmentedTabs } from "@akuna/ui";
import { Ban, CalendarCheck, FileText, Loader2, PackageCheck, RefreshCcw, Undo2 } from "lucide-react";
import { useSaleDetail } from "@/hooks/useSaleDetail";
import {
  useIssueFiscalDocument,
  useIssueReturnFiscalDocument,
  useSaleFiscalDocuments,
  useSyncFiscalDocument,
  type FiscalDocument,
} from "@/hooks/useFiscalDocuments";
import { useCreateSaleReturn, useSaleReturns } from "@/hooks/useSaleReturns";
import { getClientEnvironmentLabel, isStaging } from "@/lib/environment";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { normalizeCurrencyInput, parseCurrencyInput } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";
import { SaleNfseReviewDialog } from "@/components/erp/pos/SaleNfseReviewDialog";
import { isProductSaleItem } from "@/lib/sales/itemClassification";
import { useFiscalEnabled } from "@/hooks/useFiscalEnabled";
import { useClinicFiscalSettings } from "@/hooks/useClinicFiscalSettings";

type SaleStatus = Database["public"]["Enums"]["sale_status"];
type PaymentMethod = Database["public"]["Enums"]["payment_method"];

const PAYMENT_LABEL_KEYS: Record<string, string> = {
  DINHEIRO: "paymentSection.methods.cash",
  CARTAO_DEBITO: "paymentSection.methods.debitCard",
  CARTAO_CREDITO: "paymentSection.methods.creditCard",
  PIX: "paymentSection.methods.pix",
  A_RECEBER: "paymentSection.methods.receivable",
};

const PAYMENT_METHODS = [
  { value: "DINHEIRO", labelKey: "paymentSection.methods.cash" },
  { value: "CARTAO_DEBITO", labelKey: "paymentSection.methods.debitCard" },
  { value: "CARTAO_CREDITO", labelKey: "paymentSection.methods.creditCard" },
  { value: "PIX", labelKey: "paymentSection.methods.pix" },
  { value: "A_RECEBER", labelKey: "paymentSection.methods.receivable" },
] as const;

const SALE_STATUS_OPTIONS = [
  { value: "PAGA", labelKey: "saleDetail.statuses.paid" },
  { value: "CANCELADA", labelKey: "saleDetail.statuses.cancelled" },
  { value: "DEVOLVIDA", labelKey: "saleDetail.statuses.returned" },
] as const;

const FISCAL_EVENT_LABEL_KEYS: Record<string, string> = {
  issue_requested: "saleDetail.fiscal.events.issueRequested",
  focus_issue_response: "saleDetail.fiscal.events.issueResponse",
  focus_issue_error: "saleDetail.fiscal.events.issueError",
  focus_sync_success: "saleDetail.fiscal.events.syncSuccess",
  focus_sync_error: "saleDetail.fiscal.events.syncError",
  validation_failed: "saleDetail.fiscal.events.validationFailed",
  focus_issue_retry_without_service_municipality: "saleDetail.fiscal.events.retryWithoutServiceMunicipality",
  focus_issue_retry_with_service_municipality_code: "saleDetail.fiscal.events.retryWithServiceMunicipalityCode",
  focus_issue_retry_nfse_minimal_payload: "saleDetail.fiscal.events.retryMinimalPayload",
};

function formatFocusErrorEntry(error: unknown, labels: { code: string; correction: string }): string {
  if (!error || typeof error !== "object" || Array.isArray(error)) {
    return String(error ?? "").trim();
  }

  const entry = error as Record<string, unknown>;
  const code = String(entry.codigo ?? "").trim();
  const message = String(entry.mensagem ?? entry.error ?? "").trim();
  const correction = String(entry.correcao ?? "").trim();

  return [
    code ? `${labels.code} ${code}` : "",
    message,
    correction ? `${labels.correction}: ${correction}` : "",
  ].filter(Boolean).join(" - ");
}

function formatFiscalDocumentError(doc: FiscalDocument, labels: { code: string; correction: string }): string | null {
  const lastError = String(doc.last_error ?? "").trim();
  if (lastError && !lastError.includes("[object Object]")) {
    return lastError;
  }

  const responseErrors = doc.response_payload?.erros;
  if (Array.isArray(responseErrors)) {
    const messages = responseErrors.map((error) => formatFocusErrorEntry(error, labels)).filter(Boolean);
    if (messages.length > 0) return messages.join("; ");
  }

  const responseMessage = String(
    doc.response_payload?.mensagem ??
    doc.response_payload?.error ??
    doc.response_payload?.raw_response ??
    "",
  ).trim();

  return responseMessage || lastError || null;
}

interface SaleDetailModalProps {
  saleId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SaleDetailModal = ({
  saleId,
  open,
  onOpenChange,
}: SaleDetailModalProps) => {
  const { t } = useTranslation("erp");
  const fiscalEnabled = useFiscalEnabled();
  const { data: clinicFiscalSettings } = useClinicFiscalSettings();
  const {
    data: sale,
    isUpdating,
    updateSaleStatus,
    addSalePayment,
    updateSalePayment,
    deleteSalePayment,
  } = useSaleDetail(open ? saleId : null);
  const { hasPermission: canEditSales } = useHasPermissionSync("sales", "edit");
  const { data: fiscalDocuments = [], isLoading: isLoadingFiscalDocuments } = useSaleFiscalDocuments(open ? saleId : null, open);
  const { data: saleReturns = [], isLoading: isLoadingReturns } = useSaleReturns(open ? saleId : null, open);
  const issueFiscalDocument = useIssueFiscalDocument();
  const issueReturnFiscalDocument = useIssueReturnFiscalDocument();
  const syncFiscalDocument = useSyncFiscalDocument();
  const createSaleReturn = useCreateSaleReturn();

  const [isEditing, setIsEditing] = useState(false);
  const [fiscalTab, setFiscalTab] = useState<"NFCE" | "NFSE">("NFCE");
  const [testEnvironmentDialogOpen, setTestEnvironmentDialogOpen] = useState(false);
  const [nfseReviewOpen, setNfseReviewOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnQuantities, setReturnQuantities] = useState<Record<string, string>>({});
  const [editStatus, setEditStatus] = useState<SaleStatus>("PAGA");
  const [editPayments, setEditPayments] = useState<
    Array<{
      id: string;
      method: PaymentMethod;
      amountInput: string;
      receivedAt: string | null;
      reference: string;
      isNew: boolean;
    }>
  >([]);

  const originalPaymentsById = useMemo(() => {
    const map = new Map<string, (typeof sale)["payments"][number]>();
    for (const p of sale?.payments ?? []) {
      map.set(p.id, p);
    }
    return map;
  }, [sale?.payments]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      return;
    }
    if (!sale) return;
    const incomingStatus = (sale.status ?? "PAGA") as SaleStatus;
    const allowedStatuses = new Set<SaleStatus>(SALE_STATUS_OPTIONS.map((s) => s.value));
    setEditStatus(allowedStatuses.has(incomingStatus) ? incomingStatus : "PAGA");
    const allowedMethods = new Set(PAYMENT_METHODS.map((m) => m.value));
    setEditPayments(
      (sale.payments ?? []).map((p) => {
        const incomingMethod = p.method as PaymentMethod;
        return {
          id: p.id,
          method: allowedMethods.has(incomingMethod) ? incomingMethod : ("DINHEIRO" as PaymentMethod),
          amountInput: normalizeCurrencyInput(String(p.amount ?? 0)),
          receivedAt: p.received_at ?? null,
          reference: p.reference ?? "",
          isNew: false,
        };
      }),
    );
  }, [open, sale]);

  const receivableAmount = useMemo(() => {
    const rows = sale?.payments ?? [];
    return rows
      .filter((p) => p.method === "A_RECEBER" && !p.received_at)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }, [sale?.payments]);

  const derivedStatusLabel = useMemo(() => {
    if (!sale) return t("saleDetail.emptyValue");
    if (sale.status === "CANCELADA") return t("saleDetail.statuses.cancelled");
    if (sale.status === "DEVOLVIDA") return t("saleDetail.statuses.returned");
    if (receivableAmount > 0) return t("saleDetail.statuses.receivable");
    // cortesia: paga com 100% de desconto
    if (sale.status === "PAGA" && Number(sale.total ?? 0) === 0 && Number(sale.subtotal ?? 0) > 0) return t("saleDetail.statuses.courtesy");
    if (sale.status === "PAGA") return t("saleDetail.statuses.paid");
    return sale.status ?? "—";
  }, [sale, receivableAmount, t]);

  const hasProductItems = useMemo(
    () => !!sale?.items?.some(isProductSaleItem),
    [sale?.items],
  );

  const hasServiceItems = useMemo(
    () => !!sale?.items?.some((item) => item.item?.type === "SERVICO"),
    [sale?.items],
  );

  const isNfceConfigured = Boolean(clinicFiscalSettings?.focus_empresa_id);
  const isNfseConfigured = Boolean(
    clinicFiscalSettings?.focus_empresa_id &&
      clinicFiscalSettings.codigo_municipio_emitente &&
      clinicFiscalSettings.default_nfse_service_list_item &&
      clinicFiscalSettings.default_nfse_iss_rate != null,
  );

  const returnedQuantityBySaleItemId = useMemo(() => {
    const map = new Map<string, number>();
    for (const saleReturn of saleReturns) {
      if (saleReturn.status !== "COMPLETED") continue;
      for (const item of saleReturn.items) {
        map.set(item.sale_item_id, (map.get(item.sale_item_id) ?? 0) + Number(item.quantity ?? 0));
      }
    }
    return map;
  }, [saleReturns]);

  const returnableItems = useMemo(
    () =>
      (sale?.items ?? [])
        .filter(isProductSaleItem)
        .map((item) => {
          const soldQuantity = Number(item.quantity ?? 0);
          const returnedQuantity = returnedQuantityBySaleItemId.get(item.id) ?? 0;
          const availableQuantity = Math.max(0, soldQuantity - returnedQuantity);
          return {
            ...item,
            soldQuantity,
            returnedQuantity,
            availableQuantity,
          };
        })
        .filter((item) => item.availableQuantity > 0),
    [returnedQuantityBySaleItemId, sale?.items],
  );

  const hasReturnableProducts = returnableItems.length > 0;

  const nfceDocuments = useMemo(
    () => fiscalDocuments.filter((doc) => doc.document_type === "NFCE" && !doc.sale_return_id),
    [fiscalDocuments],
  );
  const nfseDocuments = useMemo(
    () => fiscalDocuments.filter((doc) => doc.document_type === "NFSE" && !doc.sale_return_id),
    [fiscalDocuments],
  );
  const returnDocumentsByReturnId = useMemo(() => {
    const map = new Map<string, typeof fiscalDocuments>();
    for (const doc of fiscalDocuments) {
      if (!doc.sale_return_id) continue;
      const list = map.get(doc.sale_return_id) ?? [];
      list.push(doc);
      map.set(doc.sale_return_id, list);
    }
    return map;
  }, [fiscalDocuments]);

  useEffect(() => {
    if (!open) return;
    if (hasProductItems) {
      setFiscalTab("NFCE");
      return;
    }
    if (hasServiceItems) {
      setFiscalTab("NFSE");
    }
  }, [open, hasProductItems, hasServiceItems]);

  const fiscalStatusBadge = (status: string) => {
    switch (status) {
      case "AUTHORIZED":
        return { label: t("saleDetail.fiscal.statuses.authorized"), className: "bg-green-100 text-green-900 border-green-200 dark:bg-green-900/30 dark:text-green-100 dark:border-green-900/40" };
      case "PROCESSING":
      case "PENDING_SEND":
        return { label: t("saleDetail.fiscal.statuses.processing"), className: "bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-900/40" };
      case "CANCELLED":
        return { label: t("saleDetail.fiscal.statuses.cancelled"), className: "bg-muted text-muted-foreground border-border" };
      case "ERROR":
      case "REJECTED":
        return { label: t("saleDetail.fiscal.statuses.error"), className: "bg-destructive/10 text-destructive border-destructive/20 dark:bg-destructive/20 dark:text-destructive" };
      default:
        return { label: status || "—", className: "bg-muted text-muted-foreground border-border" };
    }
  };

  const handleIssueFiscalDocument = (documentType: "NFCE" | "NFSE") => {
    if (!sale) return;
    if (isStaging()) {
      setTestEnvironmentDialogOpen(true);
      return;
    }
    if (documentType === "NFSE") {
      setNfseReviewOpen(true);
      return;
    }
    void issueFiscalDocument.mutateAsync({ saleId: sale.id, documentType });
  };

  const handleConfirmNfseIssue = () => {
    if (!sale) return;
    void issueFiscalDocument
      .mutateAsync({ saleId: sale.id, documentType: "NFSE" })
      .then(() => setNfseReviewOpen(false))
      .catch(() => undefined);
  };

  const handleIssueReturnFiscalDocument = (saleReturnId: string) => {
    if (isStaging()) {
      setTestEnvironmentDialogOpen(true);
      return;
    }
    void issueReturnFiscalDocument.mutateAsync({ saleReturnId });
  };

  const fillAllReturnQuantities = () => {
    const next: Record<string, string> = {};
    for (const item of returnableItems) {
      next[item.id] = String(item.availableQuantity);
    }
    setReturnQuantities(next);
  };

  const clearReturnForm = () => {
    setReturnReason("");
    setReturnQuantities({});
  };

  const submitReturn = async () => {
    if (!sale) return;
    const reason = returnReason.trim();
    if (!reason) {
      toast({
        title: t("saleDetail.returns.reasonRequiredTitle"),
        description: t("saleDetail.returns.reasonRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    const items = returnableItems
      .map((item) => {
        const quantity = Number(String(returnQuantities[item.id] ?? "").replace(",", "."));
        return { sale_item_id: item.id, quantity };
      })
      .filter((item) => Number.isFinite(item.quantity) && item.quantity > 0);

    if (items.length === 0) {
      toast({
        title: t("saleDetail.returns.noItemsTitle"),
        description: t("saleDetail.returns.noItemsDescription"),
        variant: "destructive",
      });
      return;
    }

    const invalid = items.find((item) => {
      const saleItem = returnableItems.find((candidate) => candidate.id === item.sale_item_id);
      return !saleItem || item.quantity > saleItem.availableQuantity;
    });

    if (invalid) {
      toast({
        title: t("saleDetail.returns.invalidQuantityTitle"),
        description: t("saleDetail.returns.invalidQuantityDescription"),
        variant: "destructive",
      });
      return;
    }

    await createSaleReturn.mutateAsync({ saleId: sale.id, reason, items });
    setReturnDialogOpen(false);
    clearReturnForm();
  };

  if (!sale && open) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex min-h-0 flex-col gap-0 p-0">
        <DialogHeader className="pl-6 pr-16 pt-6 pb-2">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle>{t("saleDetail.saleTitle", { number: sale?.sale_number })}</DialogTitle>
            <div className="flex items-center gap-2">
              {canEditSales && sale && sale.status !== "CANCELADA" && sale.status !== "DEVOLVIDA" ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 text-destructive hover:text-destructive border-destructive/40 hover:border-destructive"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={isUpdating}
                >
                  <Ban className="h-4 w-4" />
                  {t("saleDetail.cancelSale")}
                </Button>
              ) : null}
              {canEditSales && sale ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing((prev) => !prev)}
                  disabled={isUpdating}
                >
                  {isEditing ? t("saleDetail.view") : t("common.edit")}
                </Button>
              ) : null}
            </div>
          </div>
        </DialogHeader>
        {sale && (
          <>
            <ScrollArea className="flex-1 min-h-0 px-6 pb-4 !overflow-y-auto">
              <div className="space-y-4">
                <div className="space-y-1 text-sm">
                  <h4 className="font-medium text-foreground">{t("saleDetail.summary")}</h4>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("common.date")}:</span>
                    <span>
                      {sale.created_at
                        ? format(new Date(sale.created_at), "dd/MM/yyyy HH:mm", {
                          locale: ptBR,
                        })
                        : t("saleDetail.emptyValue")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("common.status")}:</span>
                    {isEditing && canEditSales ? (
                      <div className="w-40">
                        <Select value={editStatus} onValueChange={(v) => setEditStatus(v as SaleStatus)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SALE_STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {t(s.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <span>
                        {derivedStatusLabel}
                        {derivedStatusLabel === t("saleDetail.statuses.receivable") && receivableAmount > 0 ? ` (R$ ${receivableAmount.toFixed(2)})` : ""}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("common.subtotal")}:</span>
                    <span>R$ {(sale.subtotal ?? 0).toFixed(2)}</span>
                  </div>
                  {(sale.discount_amount ?? 0) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t("common.discount")}:</span>
                      <span>
                        -R$ {(sale.discount_amount ?? 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold">
                    <span>{t("common.total")}:</span>
                    <span>R$ {(sale.total ?? 0).toFixed(2)}</span>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="font-medium text-foreground mb-2">{t("receipt.items")}</h4>
                  <div className="space-y-2 rounded-md border border-border p-3 bg-muted/30">
                    {sale.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t("saleDetail.noItems")}
                      </p>
                    ) : (
                      sale.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex justify-between items-start text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <span className="font-medium">{item.item_name}</span>
                            {item.pet_name ? (
                              <span className="ml-2 rounded-full border border-primary bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">
                                {item.pet_name}
                              </span>
                            ) : null}
                            <span className="text-muted-foreground ml-1">
                              {item.quantity} × R${" "}
                              {Number(item.unit_price).toFixed(2)} = R${" "}
                              {Number(item.line_total).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <Separator />

                {fiscalEnabled && <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-foreground">{t("saleDetail.fiscal.title")}</h4>
                    {isLoadingFiscalDocuments ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : null}
                  </div>

                  <div className="rounded-md border border-border p-3 bg-muted/30 space-y-3">
                    {hasProductItems && hasServiceItems ? (
                      <p className="text-sm text-muted-foreground">
                        {t("saleDetail.fiscal.mixedSaleDescription")}
                      </p>
                    ) : null}

                    <SegmentedTabs
                      value={fiscalTab}
                      onValueChange={(v) => setFiscalTab(v as "NFCE" | "NFSE")}
                      className="space-y-3"
                      fill="screen"
                      items={(["NFCE", "NFSE"] as const).map((docType) => {
                        const isNfce = docType === "NFCE";
                        const canIssue = isNfce ? hasProductItems : hasServiceItems;
                        const docs = isNfce ? nfceDocuments : nfseDocuments;
                        const title = isNfce ? "NFC-e" : "NFS-e";
                        const isFiscalConfigured = isNfce ? isNfceConfigured : isNfseConfigured;
                        return {
                          value: docType,
                          label: docType === "NFCE" ? t("saleDetail.fiscal.tabs.nfce") : t("saleDetail.fiscal.tabs.nfse"),
                          content: (
                          <div className="space-y-3">
                            {sale.status !== "PAGA" ? (
                              <p className="text-sm text-muted-foreground">
                                {t("saleDetail.fiscal.paidOnly", { title })}
                              </p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleIssueFiscalDocument(docType)}
                                  disabled={!canIssue || issueFiscalDocument.isPending || syncFiscalDocument.isPending}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {t("saleDetail.fiscal.issueDocument", { title })}
                                </Button>
                                {!isFiscalConfigured ? (
                                  <Button size="sm" variant="ghost" asChild>
                                    <Link to="/taxes/settings" onClick={() => onOpenChange(false)}>
                                      {t("saleDetail.fiscal.configure")}
                                    </Link>
                                  </Button>
                                ) : null}
                              </div>
                            )}

                            {!canIssue ? (
                              <p className="text-sm text-muted-foreground">
                                {t("saleDetail.fiscal.noCompatibleItems", { title })}
                              </p>
                            ) : docs.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                {t("saleDetail.fiscal.noDocuments", { title })}
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {docs.map((doc) => {
                                  const status = fiscalStatusBadge(doc.internal_status);
                                  const errorMessage = formatFiscalDocumentError(doc, {
                                    code: t("saleDetail.fiscal.errorCode"),
                                    correction: t("saleDetail.fiscal.errorCorrection"),
                                  });
                                  return (
                                    <div key={doc.id} className="rounded-md border border-border bg-background p-3 space-y-2">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline">{title}</Badge>
                                          <Badge variant="outline" className={status.className}>
                                            {status.label}
                                          </Badge>
                                        </div>
                                        <div className="max-w-full truncate text-xs text-muted-foreground font-mono" title={doc.reference}>
                                          {t("saleDetail.fiscal.reference")}: {doc.reference}
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                        <div className="text-muted-foreground">
                                          {t("saleDetail.fiscal.number")}: <span className="text-foreground">{doc.fiscal_number ?? "—"}</span>
                                        </div>
                                        <div className="text-muted-foreground">
                                          {t("saleDetail.fiscal.series")}: <span className="text-foreground">{doc.fiscal_series ?? "—"}</span>
                                        </div>
                                      </div>

                                      {errorMessage ? (
                                        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive space-y-1">
                                          <p className="font-medium">{t("saleDetail.fiscal.errorTitle")}</p>
                                          <p>{errorMessage}</p>
                                        </div>
                                      ) : null}

                                      <div className="flex flex-wrap gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            void syncFiscalDocument.mutateAsync({ documentId: doc.id });
                                          }}
                                          disabled={syncFiscalDocument.isPending || issueFiscalDocument.isPending}
                                        >
                                          <RefreshCcw className="h-4 w-4 mr-2" />
                                          {t("saleDetail.fiscal.refreshStatus")}
                                        </Button>
                                        {doc.print_url ? (
                                          <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => window.open(doc.print_url as string, "_blank", "noopener,noreferrer")}
                                          >
                                            {t("saleDetail.fiscal.print")}
                                          </Button>
                                        ) : null}
                                      </div>

                                      {Array.isArray(doc.events) && doc.events.length > 0 ? (
                                        <div className="pt-1 border-t border-border space-y-1">
                                          <p className="text-xs font-medium text-muted-foreground">
                                            {t("saleDetail.fiscal.history")}
                                          </p>
                                          {doc.events.slice(0, 5).map((event) => (
                                            <div key={event.id} className="text-xs text-muted-foreground flex items-center justify-between gap-2">
                                              <span>
                                                {FISCAL_EVENT_LABEL_KEYS[event.event_type]
                                                  ? t(FISCAL_EVENT_LABEL_KEYS[event.event_type])
                                                  : event.event_type}
                                              </span>
                                              <span>
                                                {event.created_at
                                                  ? format(new Date(event.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                                                  : "—"}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          ),
                        };
                      })}
                    />
                  </div>
                </div>}

                <Separator />

                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-foreground">{t("saleDetail.returns.title")}</h4>
                      {isLoadingReturns ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
                    </div>
                    {canEditSales && sale.status === "PAGA" && hasReturnableProducts ? (
                      <Button size="sm" variant="outline" onClick={() => setReturnDialogOpen(true)}>
                        <Undo2 className="h-4 w-4 mr-2" />
                        {t("saleDetail.returns.returnProducts")}
                      </Button>
                    ) : null}
                  </div>

                  <div className="rounded-md border border-border p-3 bg-muted/30 space-y-3">
                    {saleReturns.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t("saleDetail.returns.empty")}
                      </p>
                    ) : (
                      saleReturns.map((saleReturn) => {
                        const returnDocs = returnDocumentsByReturnId.get(saleReturn.id) ?? [];
                        const authorizedDoc = returnDocs.find((doc) => doc.internal_status === "AUTHORIZED");
                        return (
                          <div key={saleReturn.id} className="rounded-md border border-border bg-background p-3 space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                                  {t("saleDetail.returns.badge")}
                                </Badge>
                                <span className="text-sm font-medium">R$ {Number(saleReturn.total ?? 0).toFixed(2)}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {saleReturn.created_at
                                  ? format(new Date(saleReturn.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                                  : "—"}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{saleReturn.reason}</p>
                            <div className="space-y-1 text-sm">
                              {saleReturn.items.map((item) => (
                                <div key={item.id} className="flex justify-between gap-2">
                                  <span>{item.item_name}</span>
                                  <span className="text-muted-foreground">
                                    {Number(item.quantity).toLocaleString("pt-BR")} × R$ {Number(item.unit_price).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {fiscalEnabled && (
                              <div className="flex flex-wrap gap-2 pt-1">
                                {nfceDocuments.some((doc) => doc.internal_status === "AUTHORIZED") ? null : (
                                  <p className="basis-full text-xs text-muted-foreground">
                                    {t("saleDetail.returns.noReferencedNfce")}
                                  </p>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleIssueReturnFiscalDocument(saleReturn.id)}
                                  disabled={!!authorizedDoc || issueReturnFiscalDocument.isPending || syncFiscalDocument.isPending}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {authorizedDoc ? t("saleDetail.returns.documentIssued") : t("saleDetail.returns.issueReturnDocument")}
                                </Button>
                                {returnDocs.map((doc) => {
                                  const status = fiscalStatusBadge(doc.internal_status);
                                  return (
                                    <Badge key={doc.id} variant="outline" className={status.className}>
                                      {t("saleDetail.returns.nfceReturnStatus", { status: status.label })}
                                    </Badge>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {!hasReturnableProducts && sale.status === "PAGA" ? (
                      <p className="text-xs text-muted-foreground">
                        {t("saleDetail.returns.noProductBalance")}
                      </p>
                    ) : null}
                  </div>
                </div>

                <Separator />

                {/* Pagamentos */}
                <div>
                  <h4 className="font-medium text-foreground mb-2">
                    {t("saleDetail.paymentMethods")}
                  </h4>
                  <div className="space-y-2 rounded-md border border-border p-3 bg-muted/30">
                    {(!isEditing ? sale.payments : editPayments).length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t("saleDetail.noPayments")}
                      </p>
                    ) : (
                      <>
                        {!isEditing
                          ? sale.payments.map((p) => (
                            <div
                              key={p.id}
                              className="flex justify-between items-baseline text-sm"
                            >
                              <span>
                                {PAYMENT_LABEL_KEYS[p.method] ? t(PAYMENT_LABEL_KEYS[p.method]) : p.method} R${" "}
                                {Number(p.amount).toFixed(2)}
                              </span>
                              {p.received_at ? (
                                <Badge variant="secondary" className="ml-2">
                                  {t("saleDetail.received")}
                                </Badge>
                              ) : null}
                            </div>
                          ))
                          : editPayments.map((p) => (
                            <div key={p.id} className="rounded-md border border-border p-3 space-y-3 bg-background">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="space-y-2">
                                  <Label>{t("saleDetail.method")}</Label>
                                  <Select
                                    value={p.method}
                                    onValueChange={(value) =>
                                      setEditPayments((prev) =>
                                        prev.map((row) => (row.id === p.id ? { ...row, method: value as PaymentMethod } : row)),
                                      )
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {PAYMENT_METHODS.map((m) => (
                                        <SelectItem key={m.value} value={m.value}>
                                          {t(m.labelKey)}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("saleDetail.amount")}</Label>
                                  <Input
                                    inputMode="decimal"
                                    value={p.amountInput}
                                    onChange={(e) => {
                                      const normalized = normalizeCurrencyInput(e.target.value);
                                      setEditPayments((prev) =>
                                        prev.map((row) => (row.id === p.id ? { ...row, amountInput: normalized } : row)),
                                      );
                                    }}
                                    placeholder="0,00"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>{t("saleDetail.received")}</Label>
                                  <div className="flex items-center gap-2 pt-2">
                                    <Checkbox
                                      id={`received-${p.id}`}
                                      checked={!!p.receivedAt}
                                      onCheckedChange={(checked) => {
                                        setEditPayments((prev) =>
                                          prev.map((row) =>
                                            row.id === p.id
                                              ? {
                                                ...row,
                                                receivedAt: checked === true ? row.receivedAt ?? new Date().toISOString() : null,
                                              }
                                              : row,
                                          ),
                                        );
                                      }}
                                    />
                                    <Label htmlFor={`received-${p.id}`} className="text-sm font-normal">
                                      {t("saleDetail.markAsReceived")}
                                    </Label>
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                  <Label>{t("saleDetail.referenceOptional")}</Label>
                                  <Input
                                    value={p.reference}
                                    onChange={(e) =>
                                      setEditPayments((prev) =>
                                        prev.map((row) => (row.id === p.id ? { ...row, reference: e.target.value } : row)),
                                      )
                                    }
                                    placeholder={t("saleDetail.referencePlaceholder")}
                                  />
                                </div>
                                <div className="flex items-end justify-end">
                                  <Button
                                    variant="destructive"
                                    onClick={() => setEditPayments((prev) => prev.filter((row) => row.id !== p.id))}
                                  >
                                    {t("common.remove")}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </>
                    )}
                  </div>
                  {isEditing && canEditSales && (
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setEditPayments((prev) => [
                            ...prev,
                            {
                              id: `new-${Date.now()}`,
                              method: "DINHEIRO" as PaymentMethod,
                              amountInput: "",
                              receivedAt: null,
                              reference: "",
                              isNew: true,
                            },
                          ])
                        }
                      >
                        {t("saleDetail.addPayment")}
                      </Button>
                    </div>
                  )}
                  <div className="flex flex-col mt-2">

                    {sale.payer_display && (
                      <div className="flex text-xs">
                        <span>{t("saleDetail.paidBy", { name: sale.payer_display })}</span>
                      </div>
                    )}
                    {sale.created_by_user_name && (
                      <div className="flex text-xs">
                        <span>{t("saleDetail.servedBy", { name: sale.created_by_user_name })}</span>
                      </div>
                    )}
                  </div>
                </div>

                {sale.appointment_id && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium text-foreground mb-2">
                        {t("saleDetail.appointment")}
                      </h4>
                      {sale.appointment_pet_names?.length ? (
                        <div className="text-sm text-muted-foreground mb-2">
                          {sale.appointment_pet_names.length === 1
                            ? t("saleDetail.appointmentPatient", { name: sale.appointment_pet_names[0] })
                            : t("saleDetail.appointmentPatients", { names: sale.appointment_pet_names.join(", ") })}
                        </div>
                      ) : null}
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to="/calendar"
                          state={{ appointmentId: sale.appointment_id }}
                          onClick={() => onOpenChange(false)}
                        >
                          <CalendarCheck className="h-4 w-4 mr-2" />
                          {t("saleDetail.viewAppointment")}
                        </Link>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>

            <DialogFooter className="px-6 py-4 border-t border-border">
              {isEditing && canEditSales ? (
                <div className="flex w-full justify-between gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditStatus((sale.status ?? "PAGA") as SaleStatus);
                      const allowedMethods = new Set(PAYMENT_METHODS.map((m) => m.value));
                      setEditPayments(
                        (sale.payments ?? []).map((p) => {
                          const incomingMethod = p.method as PaymentMethod;
                          return {
                            id: p.id,
                            method: allowedMethods.has(incomingMethod) ? incomingMethod : ("DINHEIRO" as PaymentMethod),
                            amountInput: normalizeCurrencyInput(String(p.amount ?? 0)),
                            receivedAt: p.received_at ?? null,
                            reference: p.reference ?? "",
                            isNew: false,
                          };
                        }),
                      );
                    }}
                    disabled={isUpdating}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!saleId) return;

                      // 1) Status
                      const statusChanged = (sale.status ?? "PAGA") !== editStatus;
                      if (statusChanged) {
                        await updateSaleStatus({ saleId, status: editStatus });
                      }

                      // 2) Payments diff
                      const next = editPayments;
                      const nextIds = new Set(next.filter((p) => !p.isNew).map((p) => p.id));
                      const original = sale.payments ?? [];

                      // deletions
                      const toDelete = original.filter((p) => !nextIds.has(p.id));
                      for (const p of toDelete) {
                        await deleteSalePayment(p.id);
                      }

                      // updates + inserts
                      for (const p of next) {
                        const amount = parseCurrencyInput(p.amountInput);
                        if (!amount || amount <= 0) continue;
                        if (p.isNew) {
                          await addSalePayment({
                            saleId,
                            method: p.method,
                            amount,
                            received_at: p.receivedAt ?? null,
                            reference: p.reference || null,
                          });
                          continue;
                        }
                        const orig = originalPaymentsById.get(p.id);
                        if (!orig) continue;
                        const hasChanges =
                          orig.method !== p.method ||
                          Number(orig.amount) !== amount ||
                          (orig.received_at ?? null) !== (p.receivedAt ?? null) ||
                          (orig.reference ?? "") !== p.reference;
                        if (!hasChanges) continue;
                        await updateSalePayment({
                          paymentId: p.id,
                          method: p.method,
                          amount,
                          received_at: p.receivedAt ?? null,
                          reference: p.reference || null,
                        });
                      }

                      setIsEditing(false);
                      toast({ title: t("saleDetail.updatedTitle"), description: t("saleDetail.updatedDescription") });
                    }}
                    disabled={isUpdating}
                  >
                    {t("common.save")}
                  </Button>
                </div>
              ) : (
                <Button variant="secondary" onClick={() => onOpenChange(false)}>
                  {t("common.close")}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

    <SaleNfseReviewDialog
      saleId={sale?.id ?? null}
      open={nfseReviewOpen}
      onOpenChange={setNfseReviewOpen}
      isIssuing={issueFiscalDocument.isPending}
      onConfirm={handleConfirmNfseIssue}
    />

    <Dialog open={testEnvironmentDialogOpen} onOpenChange={setTestEnvironmentDialogOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("saleDetail.testEnvironment.title")}</DialogTitle>
          <DialogDescription>
            {t("saleDetail.testEnvironment.description", { environment: getClientEnvironmentLabel() })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setTestEnvironmentDialogOpen(false)}>
            {t("common.understood")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      open={returnDialogOpen}
      onOpenChange={(nextOpen) => {
        setReturnDialogOpen(nextOpen);
        if (!nextOpen) clearReturnForm();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("saleDetail.returns.returnProducts")}</DialogTitle>
          <DialogDescription>
            {t("saleDetail.returns.dialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="return-reason">{t("saleDetail.returns.reasonLabel")}</Label>
            <Textarea
              id="return-reason"
              value={returnReason}
              onChange={(event) => setReturnReason(event.target.value)}
              placeholder={t("saleDetail.returns.reasonPlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>{t("saleDetail.returns.products")}</Label>
              <Button type="button" size="sm" variant="outline" onClick={fillAllReturnQuantities}>
                <PackageCheck className="h-4 w-4 mr-2" />
                {t("saleDetail.returns.returnAll")}
              </Button>
            </div>

            <div className="space-y-2 rounded-md border border-border p-3 bg-muted/30">
              {returnableItems.map((item) => (
                <div key={item.id} className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3 rounded-md border border-border bg-background p-3">
                  <div className="space-y-1 text-sm">
                    <div className="font-medium">{item.item_name}</div>
                    <div className="text-muted-foreground">
                      {t("saleDetail.returns.itemQuantities", {
                        sold: item.soldQuantity.toLocaleString("pt-BR"),
                        returned: item.returnedQuantity.toLocaleString("pt-BR"),
                        balance: item.availableQuantity.toLocaleString("pt-BR"),
                      })}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`return-qty-${item.id}`}>{t("saleDetail.returns.quantity")}</Label>
                    <Input
                      id={`return-qty-${item.id}`}
                      inputMode="decimal"
                      value={returnQuantities[item.id] ?? ""}
                      onChange={(event) => {
                        const rawValue = event.target.value.replace(/[^\d.,]/g, "").replace(",", ".");
                        setReturnQuantities((prev) => ({ ...prev, [item.id]: rawValue }));
                      }}
                      placeholder="0"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setReturnDialogOpen(false)} disabled={createSaleReturn.isPending}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submitReturn} disabled={createSaleReturn.isPending}>
            {createSaleReturn.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {t("saleDetail.returns.register")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("saleDetail.cancelSaleConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("saleDetail.cancelSaleConfirmDescription")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isUpdating}>
            {t("common.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isUpdating}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={async () => {
              if (!saleId) return;
              try {
                await updateSaleStatus({ saleId, status: "CANCELADA" });
                toast({ title: t("saleDetail.cancelSaleSuccess") });
                setCancelDialogOpen(false);
              } catch (err) {
                toast({
                  title: t("saleDetail.cancelSaleError"),
                  description: err instanceof Error ? err.message : undefined,
                  variant: "destructive",
                });
              }
            }}
          >
            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : t("saleDetail.cancelSale")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
